import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Pencil, School as SchoolIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, PrimaryButton, GhostButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { API, api, errMsg } from "@/lib/api";

const EMPTY = { name: "", city: "", contact_email: "", active: true };

function SchoolDialog({ school, open, onOpenChange, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(school ? { name: school.name, city: school.city || "", contact_email: school.contact_email || "", active: school.active } : EMPTY);
  }, [open, school]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (school) await api.put(`/admin/schools/${school.id}`, form);
      else await api.post("/admin/schools", form);
      toast.success("School saved");
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl sm:max-w-md" data-testid="school-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{school ? "Edit school" : "Add partner school"}</DialogTitle><DialogDescription>The school's name and logo appear on reports for students linked to it.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>School name</Label><Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name-input" /></div>
          <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="school-city-input" /></div>
          <div className="space-y-2"><Label>Contact email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="school-email-input" /></div>
          <label className="flex items-center justify-between"><span className="text-sm font-semibold">Active</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="school-active-switch" /></label>
          <PrimaryButton type="submit" busy={busy} className="w-full" data-testid="school-save-button">Save school</PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SchoolCard({ school, onEdit, onUploaded }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/admin/schools/${school.id}/logo`, fd); toast.success("Logo uploaded"); onUploaded(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="card-surface p-5 flex flex-col gap-4" data-testid={`school-card-${school.id}`}>
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-xl border border-[#E9DFE7] bg-[#FAF8FB] flex items-center justify-center overflow-hidden">
          {school.logo_path ? <img src={`${API}/public/schools/${school.id}/logo?v=${school.logo_updated_at}`} alt={`${school.name} logo`} className="h-full w-full object-contain" /> : <SchoolIcon className="h-6 w-6 text-[#8a7686]" />}
        </div>
        <div className="min-w-0">
          <p className="font-display font-bold truncate">{school.name}</p>
          <p className="text-xs text-[#635060]">{school.city || "-"} - {school.student_count} students - {school.active ? "active" : "inactive"}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <GhostButton onClick={() => onEdit(school)} data-testid={`school-edit-${school.id}`}><Pencil className="h-4 w-4" />Edit</GhostButton>
        <GhostButton onClick={() => input.current?.click()} disabled={busy} data-testid={`school-upload-logo-${school.id}`}><Upload className="h-4 w-4" />{busy ? "Uploading..." : "Upload logo"}</GhostButton>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={upload} data-testid={`school-logo-input-${school.id}`} />
      </div>
    </div>
  );
}

export default function Schools() {
  const { data, reload } = useFetch("/admin/schools");
  const [dialog, setDialog] = useState({ open: false, school: null });
  return (
    <div data-testid="schools-page">
      <PageHeader eyebrow="Branding" title="Partner schools" description="Assign a school when creating an account; its name and logo then appear on that student's report."
        actions={<PrimaryButton onClick={() => setDialog({ open: true, school: null })} data-testid="add-school-button"><Plus className="h-4 w-4" />Add school</PrimaryButton>} />
      {data?.length === 0 && <p className="card-surface p-10 text-center text-[#8a7686]" data-testid="schools-empty">No partner schools yet.</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data?.map((s) => <SchoolCard key={s.id} school={s} onEdit={(school) => setDialog({ open: true, school })} onUploaded={reload} />)}
      </div>
      <SchoolDialog open={dialog.open} school={dialog.school} onOpenChange={(open) => setDialog({ ...dialog, open })} onDone={reload} />
    </div>
  );
}
